import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { GoogleMap, Polygon, InfoWindow } from '@react-google-maps/api';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  useTheme,
  useMediaQuery,
  Typography,
  Stack,
  Box,
  SvgIcon
} from '@mui/material';
import NotificationBox from '../../components/NotificationBox';
import { useNotifications } from '../../hooks/useNotifications';
import { Pagination } from '@mui/material';
import {
  Block,
  Delete,
  Edit,
  History,
  LockOpen,
  Timeline,
  VolumeUp,
  RouteOutlined,
  NoteAdd,
  RestartAlt
} from '@mui/icons-material';
import moment, { Moment } from 'moment';
import clsx from 'clsx';

import Title from '../../components/Title';
import { useWebSocketContext } from '../../containers/WebSocketProvider';
import ContractsHistoryModal from '../../modals/ContractsHistoryModal';
import { TripTimeModal } from '../../modals/TripTimeModal';
import {
  getZonesAsync,
  getZoneTypesAsync,
  selectZones
} from '../../redux/slices/zonesSlice';
import { Zone } from '../../resources/types/zonesTypes';
import { formatDate } from '../../resources/functions';
import {
  deleteScooterAsync,
  formatCDScooterAsync,
  getScootersAsync,
  lockScooterAsync,
  selectScooters,
  signalScooterAsync,
  updateScooterInfoAsync
} from '../../redux/slices/scootersSlice';
import MapMarker from '../../containers/MapMarker';
import ScooterMarkerWithBadge from '../../containers/MapMarker/ScooterMarkerWithBadge';
import ParkingMarker from '../../components/ParkingMarker';
import { getParkingsAsync, selectParkings } from '../../redux/slices/parkingsSlice';
import Collapse from '../../components/Collapse';
import TableCell from '../../components/TableCell';
import ScooterInfoWindow from '../../components/ScooterInfoWindow';
import { Scooter } from '../../resources/types/scootersTypes';
import {
  TripHistoryDetails,
  TripTimeDialogState
} from '../../resources/types/contractsTypes';
import {
  exitContractAsync,
  getActiveContractsAsync,
  selectContracts
} from '../../redux/slices/contractsSlice';
import ScooterCounters from '../../components/ScooterCounters';
import usePermissions from '../../hooks/usePermissions';
import useFormInput from '../../hooks/useFormInput';
import MapFiltersBar from '../../components/MapFiltersBar/MapFiltersBar';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import { selectAuth } from '../../redux/slices/authSlice';
import { selectCities } from '../../redux/slices/citiesSlice';
import Select from '../../components/Select';
import { SelectOption } from '../../components/Select/interface';
import { MapFilters, ScootersModalState, ScooterStatus } from './interface';
import ScootersModal from '../../modals/ScootersModal';
import Dot from '../../components/Dot';
// import MapLegend from './MapLegend';
import DateRangePicker from '../../components/DateRangePicker';
import { DateRange } from '../../types/dateRange';
import { ReactComponent as CrownIcon } from '../../img/crown.svg';
import { classes, defaultCenter, LIMIT, zonesColors } from './constants';
import { TripCoordinatesModal } from '../../modals/TripCoordinatesModal/TripCoordinatesModal';
import ScooterNotesModal from '../../modals/ScooterNotesModal';
import ScooterSettingsModal from '../../modals/ScooterSettingsModal';
import {
  getDotColor,
  getDotTooltip,
  getFilledSquareMarkerIcon
} from '../../helpers/scooter-markers-helper';
import { darkMapStyles } from '../../resources/constants';
import FullscreenMapButton from '../../components/FullscreenMapButton/FullscreenMapButton';

const hasInactivityFlag = (battery: number | string) => {
  return String(battery).includes('*');
};

const hasNoCoordinates = (battery: number | string) => {
  return String(battery).includes('!');
};

const cleanBatteryValue = (battery: number | string) => {
  let batteryStr = String(battery);
  // Видаляємо "*" (інактивність) та "!" (немає координат) незалежно від порядку
  while (batteryStr.startsWith('*') || batteryStr.startsWith('!')) {
    if (batteryStr.startsWith('*')) {
      batteryStr = batteryStr.substring(1);
    } else if (batteryStr.startsWith('!')) {
      batteryStr = batteryStr.substring(1);
    }
  }
  return batteryStr;
};

const cleanScooterName = (name: string) => {
  // Видаляємо всі спеціальні символи "!" та "*" з номера самоката
  return String(name).replace(/[!*]/g, '');
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.header}`]: {
    display: 'flex',
    alignItems: 'center'
  },

  [`& .${classes.uploadButton}`]: {
    marginLeft: 20
  },

  [`& .${classes.blockContainer}`]: {
    marginTop: 20
  },

  [`& .${classes.saveButton}`]: {
    margin: 20
  },

  [`& .${classes.infoWindow}`]: {
    padding: 6,
    paddingRight: 25,
    fontSize: 12,
    color: 'gray',
    whiteSpace: 'nowrap'
  },

  [`& .${classes.collapseLabel}`]: {
    padding: '10px 20px'
  },

  [`& .${classes.tableBody}`]: {
    '& td': {
      verticalAlign: 'top'
    }
  },

  [`& .${classes.filters}`]: {
    padding: '8px 20px !important'
  },

  [`& .${classes.lock}`]: {
    color: theme.palette.error.main
  },

  [`& .${classes.scooterIcons}`]: {
    display: 'inline-flex'
  },

  [`& .${classes.routeLink}`]: {
    color: theme.palette.primary.main,
    textDecoration: 'underline'
  },

  [`& .${classes.contractImages}`]: {
    display: 'flex'
  },

  [`& .${classes.contractImage}`]: {
    width: 64,
    height: 64,
    borderRadius: 8,
    '& + &': {
      marginLeft: 12
    },
    '& img': {
      width: '100%',
      height: '100%'
    }
  },

  [`& .${classes.tabButton}`]: {
    margin: 0,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent',
    borderRadius: 0,
    minWidth: '130px',
    '&.active': {
      borderBottomColor: theme.palette.mode === 'light' ? '#3D5BD4' : theme.palette.primary.main
    }
  },

  [`& .${classes.typeSelector}`]: {
    width: 180
  }
}));

const ZonesPage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const auth = useSelector(selectAuth);
  const admins = useSelector(selectAdmins);
  const cities = useSelector(selectCities);
  const parkingsState = useSelector(selectParkings);

  const checkPermission = usePermissions();

  const { sendSocketMessage } = useWebSocketContext();
  const { newNotices, hasNotifications, clearNotifications, forceRefresh } = useNotifications();

  // Responsive breakpoints for map height
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));

  const [scootersCity, setScootersCity] = useState(admins.currentCity ?? '');
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [mapHeightPx, setMapHeightPx] = useState(600);
  const [scootersModalState, setScootersModalState] =
    useState<ScootersModalState>({ open: false });
  const [scooterNotesModal, setScooterNotesModal] = useState<{
    open: boolean;
    scooter?: Scooter;
  }>({ open: false });
  const [scooterSettingsModal, setScooterSettingsModal] = useState<{
    open: boolean;
    scooter?: Scooter;
  }>({ open: false });
  // const [batteryLevel, setBatteryLevel] = useState('');
  const [filteredByScooterName, setFilteredByScooterName] = useState('');
  const [scooterNumber, setScooterNumber] = useState('');

  const [mapFilters, setMapFilters] = useState<MapFilters>({});
  const searchScooter = useFormInput('');
  
  // Filter states
  const [showHidden, setShowHidden] = useState(false);
  const [showZones, setShowZones] = useState(true);
  const [minBatteryLevel, setMinBatteryLevel] = useState(0);

  useEffect(() => {
    if (!checkPermission('zones')) {
      return;
    }

    dispatch(getZonesAsync(true));
    dispatch(getZoneTypesAsync());
    dispatch(getParkingsAsync(true));
  }, [checkPermission, dispatch]);

  // Calculate map height in pixels based on viewport and breakpoint
  useEffect(() => {
    const calculateMapHeight = () => {
      if (isMapFullscreen) {
        setMapHeightPx(window.innerHeight);
        return;
      }

      let offset = 400; // default for lg+
      if (isXs) offset = 240;
      else if (isSm) offset = 280;
      else if (isMd) offset = 340;

      const calculatedHeight = window.innerHeight - offset;
      const minHeight = (isXs || isSm) ? 600 : 400;
      setMapHeightPx(Math.max(minHeight, calculatedHeight));
    };

    calculateMapHeight();

    window.addEventListener('resize', calculateMapHeight);
    return () => window.removeEventListener('resize', calculateMapHeight);
  }, [isMapFullscreen, isXs, isSm, isMd]);

  const getScootersBySockets = useCallback(() => {
    if (!checkPermission('scooters') || !sendSocketMessage) {
      return;
    }
    console.log('-=-=- getScootersBySockets', {
      cityId: scootersCity,
      ...mapFilters,
      // free: false,
      // status: ScooterStatus.CHARGING,
      name: filteredByScooterName,
      // battery_value: +batteryLevel,
      ...(minBatteryLevel > 0 && { lt_battery: minBatteryLevel })
    });
    sendSocketMessage('scooters', {
      cityId: scootersCity,
      ...mapFilters,
      // free: false,
      // status: ScooterStatus.CHARGING,
      name: filteredByScooterName,
      // battery_value: +batteryLevel,
      ...(minBatteryLevel > 0 && { lt_battery: minBatteryLevel })
    });
  }, [
    checkPermission,
    filteredByScooterName,
    minBatteryLevel,
    mapFilters,
    scootersCity,
    sendSocketMessage
  ]);

  useEffect(() => {
    if (!checkPermission('scooters') || !sendSocketMessage) {
      return;
    }

    getScootersBySockets();

    const interval = setInterval(() => {
      getScootersBySockets();
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [
    checkPermission,
    dispatch,
    scootersCity,
    sendSocketMessage,
    minBatteryLevel,
    filteredByScooterName,
    getScootersBySockets
  ]);

  const [isActiveContracts, setIsActiveContracts] = useState(false);
  const [page, setPage] = useState(1);

  const onChangeIsActiveContracts = () => {
    setIsActiveContracts(!isActiveContracts);
    setPage(1);
  };

  const onChangePage = (e: any, nextPage: number) => {
    setPage(nextPage);
  };

  // CONTRACTS TAB
  const adminInfo = useSelector(selectAdmins);
  const [userPhone, setUserPhone] = useState('');
  const [scooterName, setScooterName] = useState('');
  const [tripDate, setTripDate] = useState<DateRange<Moment | null>>([
    moment().set({ date: 1, hours: 0, minutes: 0, seconds: 0 }),
    moment().set({ hours: 23, minutes: 59, seconds: 59 })
  ]);
  const currentCityId = useMemo(() => {
    const city = cities.items.find(item => item._id === adminInfo.currentCity);
    if (city) {
      return city?._id;
    }
    return adminInfo?.currentCity;
  }, [adminInfo.currentCity, cities.items]);

  const [tripCity, setTripCity] = useState(currentCityId);
  const [tripTimeDialogState, setTripTimeDialogState] =
    useState<TripTimeDialogState>({
      open: false
    });

  const [tripCoordinatesDialogState, setTripCoordinatesDialogState] = useState<{
    open: boolean;
    tripId?: string;
  }>({
    open: false
  });

  const [filterByHours, setFilterByHours] = useState<{
    from: Date | null;
    to: Date | null;
  }>({
    from: moment()
      .set({
        hours: 0,
        minutes: 0
      })
      .toDate(),
    to: moment()
      .set({
        hours: 23,
        minutes: 59
      })
      .toDate()
  });
  const [isTimeFiltersTouched, setIsTimeFiltersTouched] = useState({
    from: false,
    to: false
  });

  const handleFilterByHoursFrom = (val: Date | null) => {
    setIsTimeFiltersTouched(prev => ({ ...prev, from: true }));
    setFilterByHours(prevVal => {
      return {
        ...prevVal,
        from: moment(val)
          .set({
            month: moment(tripDate[0]).get('month'),
            day: moment(tripDate[0]).get('day')
          })
          .toDate()
      };
    });
  };

  const handleFilterByHoursTo = (val: Date | null) => {
    setIsTimeFiltersTouched(prev => ({ ...prev, to: true }));
    setFilterByHours(prevVal => {
      return {
        ...prevVal,
        to: moment(val)
          .set({
            month: moment(tripDate[1]).get('month'),
            day: moment(tripDate[1]).get('day')
          })
          .toDate()
      };
    });
  };

  const getActiveContracts = useCallback(() => {
    if (!checkPermission('contracts')) {
      return;
    }

    dispatch(
      getActiveContractsAsync({
        active: isActiveContracts,
        page,
        limit: LIMIT,
        userPhone,
        scooterName,
        cityId: tripCity || 'all',
        from:
          isTimeFiltersTouched.from === true &&
          !isNaN(moment(filterByHours.from).unix() * 1000)
            ? moment(filterByHours.from).unix() * 1000
            : tripDate[0]?.toDate().setHours(0, 0, 0),
        to:
          isTimeFiltersTouched.to === true &&
          !isNaN(moment(filterByHours.to).unix() * 1000)
            ? moment(filterByHours.to).unix() * 1000
            : tripDate[1]?.toDate().setHours(23, 59, 59)
      })
    );
  }, [
    checkPermission,
    dispatch,
    isActiveContracts,
    page,
    userPhone,
    scooterName,
    tripCity,
    tripDate,
    filterByHours,
    isTimeFiltersTouched
  ]);

  useEffect(() => {
    if (!checkPermission('contracts')) {
      return;
    }

    getActiveContracts();
  }, [checkPermission, getActiveContracts]);

  const zonesState = useSelector(selectZones);
  const scooters = useSelector(selectScooters);
  const { activeContracts } = useSelector(selectContracts);

  const sortedScooters = useMemo(() => {
    let scootersList = [...(scooters?.items ?? [])];
    return (
      scootersList.sort((a, b) => parseInt(a.name) - parseInt(b.name)) ?? []
    );
  }, [scooters?.items]);

  const [mapCenter, setMapCenter] = useState(defaultCenter);

  useEffect(() => {
    let nextMapCenter;

    if (auth.info?.type === 'superAdmin') {
      const city = cities.items.find(item => item._id === admins.currentCity);
      if (city?.lat && city.lon) {
        nextMapCenter = {
          lat: city.lat,
          lng: city.lon
        };
      }
    } else if (admins.info && admins.currentCity) {
      const city = admins.info.cities.find(
        item => item._id === admins.currentCity
      );
      if (city?.lat && city.lon) {
        nextMapCenter = {
          lat: city.lat,
          lng: city.lon
        };
      }
    }

    if (!nextMapCenter) {
      setMapCenter(defaultCenter);
    } else {
      setMapCenter(nextMapCenter);
    }
  }, [admins.currentCity, admins.info, auth.info, cities.items]);

  const [selectedZone, setSelectedZone] = useState<{
    position: google.maps.LatLng | null;
    zone: Zone | null;
  }>({ position: null, zone: null });
  const toggleInfoWindow = (
    zone: Zone | null,
    e: google.maps.MapMouseEvent | null
  ) => {
    setSelectedZone({ position: e ? e.latLng : null, zone });
  };

  const [contractsHistoryDialogState, setContractsHistoryDialogState] =
    useState<{ open: boolean; scooterId?: string; userId?: string }>({
      open: false
    });

  const toggleScooterViewed = useCallback(
    (scooter: Scooter) => () => {
      dispatch(
        updateScooterInfoAsync(scooter._id, { viewed: !scooter.viewed })
      );
    },
    [dispatch]
  );

  const exitContract = useCallback(
    (contrId: string) => () => {
      if (window.confirm(t('control.confirmEndTrip'))) {
        dispatch(exitContractAsync(contrId, getActiveContracts));
      }
    },
    [dispatch, getActiveContracts]
  );

  const paginationCount = useMemo(
    () => Math.ceil(activeContracts.count / LIMIT),
    [activeContracts.count]
  );

  const signalScooter = useCallback(
    (scooter: Scooter) => async () => {
      await dispatch(signalScooterAsync(scooter._id, { value: true }));
    },
    [dispatch]
  );

  const citiesOptions = useMemo<SelectOption[]>(
    () => [
      {
        label: t('control.all'),
        value: ''
      },
      ...cities.items.map(city => ({
        label: city.cityName,
        value: city._id
      }))
    ],
    [cities.items]
  );

  const onSubmitContractsFilters = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const { userPhone, scooterName } = Object.fromEntries(
        new FormData(event.currentTarget).entries()
      );

      setUserPhone(userPhone as string);
      setScooterName(scooterName as string);
    },
    []
  );

  // const getDotColor = useCallback(
  //   (scooter: Scooter) => {
  //     if (scooter.status === ScooterStatus.HIDDEN) {
  //       return theme.palette.grey[700];
  //     }

  //     if (!scooter.connected) {
  //       return theme.palette.violet.main;
  //     }

  //     if (!scooter.free) {
  //       return theme.palette.red.main;
  //     }

  //     if (!scooter.viewed) {
  //       return theme.palette.black.main;
  //     }

  //     if (scooter.free && !scooter.lock) {
  //       return theme.palette.yellow.main;
  //     }

  //     if (scooter.free && scooter.connected && scooter.lock) {
  //       return theme.palette.green.main;
  //     }

  //     return theme.palette.violet.main;
  //   },
  //   [theme]
  // );

  const isSuperAdmin = useMemo(() => {
    return auth.info?.type === 'superAdmin';
  }, [auth.info?.type]);

  // const handleBatteryLevel = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   setBatteryLevel(event.target.value);
  // };

  const handleScooterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setScooterNumber(event.target.value);
  };

  const handleTripDate = useCallback(
    (nextValue: DateRange<Moment>) => {
      setTripDate(nextValue);
    },
    [setTripDate]
  );

  const handleTripDetailsClick = (
    event: any,
    createdAt?: string,
    closedAt?: string,
    cost?: string | number,
    status?: string,
    cableImgCoords?: {
      lat?: string;
      lon?: string;
    },
    tripHistoryDetails?: TripHistoryDetails
  ) => {
    event.stopPropagation();
    setTripTimeDialogState({
      open: true,
      createdAt,
      closedAt,
      cost,
      status,
      cableImgCoords,
      tripHistoryDetails
    });
  };

  const handleTripCoordinatesClick = (event: any, tripId?: string) => {
    event.stopPropagation();
    setTripCoordinatesDialogState({
      open: true,
      tripId
    });
  };

  console.log('-=- scooters', scooters);

  return (
    <Root>
      <div className={classes.header}>
        <Title>{t('nav.management')}</Title>
      </div>
      <Paper sx={{ 
        border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', 
        borderRadius: 2, 
        padding: { xs: '8px', sm: '12px' }, 
        margin: { xs: '8px 0', sm: '12px 0' } 
      }}>
        <ScooterCounters
          mapFilters={mapFilters}
          onFilterSet={setMapFilters}
        />
      </Paper>
      <MapFiltersBar
        searchValue={searchScooter.value}
        onSearchChange={(value) => searchScooter.onChange({ target: { value } } as any)}
        showHidden={showHidden}
        onShowHiddenChange={setShowHidden}
        showZones={showZones}
        onShowZonesChange={setShowZones}
        minBatteryLevel={minBatteryLevel}
        onMinBatteryLevelChange={setMinBatteryLevel}
      />
      {checkPermission('map') && (
        <Paper sx={{ border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', borderRadius: 2, padding: '20px' }}>
          <Box ref={mapContainerRef} sx={{ position: 'relative', minHeight: '600px' }}>
            <FullscreenMapButton 
              containerRef={mapContainerRef} 
              onFullscreenChange={setIsMapFullscreen}
            />
            {/* Доки google.maps.Map не готовий, карту не монтуємо: <GoogleMap>
                падає в componentDidMount, а разом з ним зникає вся сторінка.
                Сюди ж потрапляє випадок, коли Google не піднявся за таймаут. */}
            {!admins.isGoogleApiInit ? (
              <Box
                sx={{
                  height: `${mapHeightPx}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography color='text.secondary'>
                  Карта завантажується…
                </Typography>
              </Box>
            ) : (
            <GoogleMap
              center={mapCenter}
              mapContainerStyle={{
                width: '100%',
                height: `${mapHeightPx}px`
              }}
              options={{
                disableDefaultUI: true,
                zoomControl: true,
                fullscreenControl: false,
                styles: theme.palette.mode === 'dark' ? darkMapStyles : undefined
              }}
              zoom={11}
            >
              {parkingsState.parkings.map(parking => (
                <ParkingMarker key={parking._id} parking={parking} />
              ))}
              {showZones && zonesState.zones.map(zone => (
                <Polygon
                  key={zone._id}
                  options={zonesColors[zone.type] || zonesColors.green}
                  paths={zone.coordinates.map(coordinate => ({
                    lat: coordinate[0],
                    lng: coordinate[1]
                  }))}
                  onClick={e => toggleInfoWindow(zone, e)}
                />
              ))}
              {selectedZone.zone && selectedZone.position && (
                <InfoWindow
                  position={selectedZone.position}
                  onCloseClick={() => toggleInfoWindow(null, null)}
                >
                  <>
                    {t('control.zoneName')}: {selectedZone.zone.name}
                    <br />
                    {t('control.zoneType')}: {selectedZone.zone.type}
                  </>
                </InfoWindow>
              )}
              {scooters?.items?.map((scooter, index) => {
                const isBusMarker =
                  scooter.name.includes('AVTO') ||
                  scooter.name === 'BUS-IF-S' ||
                  scooter.name === 'BUS_KOL' ||
                  scooter.name === 'BUS-IF-T';

                // Якщо "Показати приховані" увімкнено - показуємо тільки приховані
                if (showHidden && scooter.status !== ScooterStatus.HIDDEN) {
                  return null;
                }

                // Якщо "Показати приховані" вимкнено - ховаємо приховані
                if (!showHidden && scooter.status === ScooterStatus.HIDDEN) {
                  return null;
                }

                // Очищаємо номер самоката від спеціальних символів для пошуку
                const cleanedScooterName = cleanScooterName(scooter.name);
                const cleanedSearchValue = cleanScooterName(searchScooter.value);

                // Фільтрація за пошуковим запитом (ігноруємо спеціальні символи)
                if (searchScooter.value && !cleanedScooterName.includes(cleanedSearchValue)) {
                  return null;
                }

                const hasInactivity = hasInactivityFlag(scooter.battery);
                const noCoordinates = hasNoCoordinates(scooter.battery);
                const cleanBattery = cleanBatteryValue(scooter.battery);
                const batteryDisplay = hasInactivity ? `24h ${cleanBattery}` : cleanBattery;

                // Перевіряємо чи повністю співпадає номер (для автоматичного відкриття InfoWindow)
                const isExactMatch = cleanedScooterName === cleanedSearchValue;

                const markerIcon = getFilledSquareMarkerIcon(scooter, scooter.name);
                if (!markerIcon) {
                  return null;
                }

                return (
                  scooter.coords && (
                    <ScooterMarkerWithBadge
                      forceInfoWindowOpen={isExactMatch}
                      icon={{
                        url: markerIcon,
                        labelOrigin: new window.google.maps.Point(16, 16),
                        scaledSize: new window.google.maps.Size(32, 32)
                      }}
                      key={scooter._id}
                      position={{
                        lat: scooter.coords.lat,
                        lng: scooter.coords.lon
                      }}
                      batteryLevel={
                        isBusMarker ? ' ' : cleanBattery ?? 'null'
                      }
                      hasInactivityBadge={!isBusMarker && hasInactivity}
                      hasNoCoordinatesBadge={!isBusMarker && noCoordinates}
                      hasForceLocationBadge={!isBusMarker && (Boolean(scooter.trackerCoords) || Boolean(scooter.hasForceCoords))}
                      {...(!scooter.connected
                        ? { zIndex: 5000 + index }
                        : { zIndex: isBusMarker ? 9000 + index : index })}
                    >
                      <ScooterInfoWindow 
                        scooter={scooter} 
                        onOpenSettings={() => setScooterSettingsModal({ open: true, scooter })}
                      />
                    </ScooterMarkerWithBadge>
                  )
                );
              })}
            </GoogleMap>
            )}
          </Box>
        </Paper>
      )}
      <ScootersModal
        {...scootersModalState}
        isSuperAdmin={isSuperAdmin}
        onClose={() =>
          setScootersModalState({
            open: false,
            scooter: undefined
          })
        }
      />
      <ContractsHistoryModal
        open={contractsHistoryDialogState.open}
        onClose={() => setContractsHistoryDialogState({ open: false })}
        userId={contractsHistoryDialogState.userId}
      />
      <TripTimeModal
        open={tripTimeDialogState.open}
        onClose={() => setTripTimeDialogState({ open: false })}
        createdAt={tripTimeDialogState.createdAt}
        closedAt={tripTimeDialogState.closedAt}
        cost={tripTimeDialogState.cost}
        status={tripTimeDialogState?.status}
        cableImgCoords={tripTimeDialogState?.cableImgCoords}
        tripHistoryDetails={tripTimeDialogState?.tripHistoryDetails}
      />
      <TripCoordinatesModal
        open={tripCoordinatesDialogState.open}
        onClose={() => setTripCoordinatesDialogState({ open: false })}
        tripCity={tripCity}
        tripId={tripCoordinatesDialogState.tripId}
      />
      <ScooterNotesModal
        open={scooterNotesModal.open}
        onClose={() => setScooterNotesModal({ open: false })}
        isSuperAdmin={isSuperAdmin}
        scooter={scooterNotesModal?.scooter}
      />
      <ScooterSettingsModal
        open={scooterSettingsModal.open}
        onClose={() => setScooterSettingsModal({ open: false })}
        isSuperAdmin={isSuperAdmin}
        scooter={scooterSettingsModal?.scooter}
        container={mapContainerRef.current ?? undefined}
      />
      {newNotices.length > 0 && (
        <NotificationBox 
          notices={newNotices} 
          onClose={clearNotifications}
          onRefresh={forceRefresh}
        />
      )}
    </Root>
  );
};

export default ZonesPage;
